import { ImageScaleView, ViewOpts } from '@play-co/timestep-core/ui';
import { now, user } from 'src/StateObserver';
import { downscale } from 'src/conf/scale';
import { RadialShine } from 'src/game/components/shared/RadialShine';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import { createEmitter } from 'src/lib/Emitter';
import {
  canSeeLeague,
  getLeagueScore,
  hasJoinedLeague,
} from 'src/replicant/match2/leagues/leagues.getters';
import { canSeeArena } from 'src/replicant/championsLeague/championsLeague.getters';
import i18n from 'src/lib/i18n/i18n';
import { getPrioritizedLeagueId } from '../championsLeague.utils';
import { getLeagueLeaderboard } from 'src/game/minigame/match2-timestep/leagues/league.sequences';

const assets = {
  base: 'assets/ui/championsLeague/buttons/button_leagues.png',
};

export class ChampionsLeagueButtonArena extends ButtonScaleView {
  private rank: LangBitmapFontTextView;
  private leagueIdCache: string | undefined;

  constructor(opts: ViewOpts & { onClick?: () => Promise<void> }) {
    super({
      ...opts,
      width: 200,
      height: 200,
      pressedOffsetY: 5,
      onClick: opts.onClick,
      tracking: {
        feature: Feature.LEAGUES._,
        $subFeature: Feature.LEAGUES.ROOM_BUTTON,
      },
    });

    new RadialShine({
      superview: this,
      scale: 1.7,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
    });

    new ImageScaleView({
      superview: this,
      image: assets.base,
      scaleMethod: 'contain',
      width: downscale(294),
      height: downscale(261),
      centerOnOrigin: true,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5,
    });

    new LangBitmapFontTextView({
      superview: this,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Title',
      width: 100,
      height: 50,
      x: this.style.width * 0.5 - 3,
      y: this.style.height - 45,
      localeText: () => i18n('championsLeague.arena'),
    });

    this.rank = new LangBitmapFontTextView({
      superview: this,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Title',
      width: 60,
      height: 60,
      x: this.style.width * 0.5 - 4,
      y: 55,
    });

    // This button should start hidden, and only show up after first refresh, if suitable
    this.hide();

    createEmitter(this, ({ user }) => {
      const leagueId = getPrioritizedLeagueId();
      return {
        arena: canSeeArena(user, now()),
        canSee: canSeeLeague(user),
        leagueId,
        score: getLeagueScore(user, leagueId),
      };
    }).addListener(this.refresh);
  }

  private refresh = async (args: {
    arena: boolean;
    canSee: boolean;
    leagueId: string;
    score: number | undefined;
  }) => {
    const { arena, canSee, leagueId } = args;

    // Only show this button if arena is available
    if (!arena) {
      this.hide();
      return;
    }

    this.show();

    const hasJoined = hasJoinedLeague(user());
    const showRank = canSee && hasJoined;

    this.rank.updateOpts({ visible: showRank });

    if (showRank) this.updateRank(leagueId);
  };

  private async updateRank(leagueId: string) {
    const cache = getLeagueLeaderboard();
    let playerRank: number | undefined;

    // Check if the cache is already populated for the current league.
    if (leagueId === this.leagueIdCache) {
      playerRank = cache.getCached()?.playerRank;
    }

    this.leagueIdCache = leagueId;

    // If not or if the player rank is -1, invalidate and refetch the leaderboard cache.
    while (!playerRank || playerRank === -1) {
      await cache.invalidate();
      const result = await cache.get();
      playerRank = result.playerRank;
    }

    this.rank.localeText = () => `${playerRank ?? '-'}`;
  }
}
